import clsx from "clsx";
// MUI
import { Grid, GridProps } from "@mui/material";
// Helpers
import { useIsWashoutEditable } from "@/Washouts/hooks/WashoutReview/useIsWashoutEditable";
// Tables
import EmployeeMetaSection from "@/Washouts/components/EmployeeMetaSection";
import Compensation from "@/Washouts/components/tables/Common/CompensationTable";
import TechnicianPerformanceTable from "@/Washouts/components/tables/Technicians/TechnicianPerformanceTable";
import SpiffsBonus from "@/Washouts/components/tables/Common/SpiffsBonusTable";
// Types
import { ITechniciansWashoutContent } from "@/Washouts/types/technicians";
import { BASE_TABLE_PROPS } from "@/Washouts/constants";
import LongevityCard from "@/Washouts/components/tables/Common/LongevityCard";

const prepareRowsForTable = (rows: Record<string, object>[] = []) => {
	return rows?.map?.((row) => {
		return {
			...row,
			id: row?._id,
		};
	});
};

const PerformanceSection = ({ model = undefined, isLoading = false }: Readonly<ITechniciansWashoutContent>) => (
	<TechnicianPerformanceTable
		data={prepareRowsForTable(model?.flaggedTimePerformance?.repairOrders)}
		title="Flagged Time Performance"
		dataPresent={Boolean(model)}
		divider={true}
		loading={isLoading}
		tableProps={{ ...BASE_TABLE_PROPS, maxRows: 16 }}
	/>
);

const PendingRepairOrdersSection = ({ model = undefined, isLoading = false }: Readonly<ITechniciansWashoutContent>) => (
	<TechnicianPerformanceTable
		data={prepareRowsForTable(model?.pendingRepairOrders?.repairOrders)}
		dataPresent={Boolean(model)}
		divider={true}
		loading={isLoading}
		title="Pending Repair Orders and Jobs"
		tableProps={{ ...BASE_TABLE_PROPS }}
	/>
);

const LongevitySection = ({ model = undefined, isLoading = false, ...rest }: Readonly<ITechniciansWashoutContent & GridProps>) => {
	return (
		<Grid item {...rest}>
			<LongevityCard
				data={[{ ...model?.longevity }]}
				loading={isLoading}
				minRows={1}
				columnsOverride={[
					{ field: "hiredDate", width: -1 },
					{ field: "years", width: -1 },
					{ field: "compensation", width: -1 },
				]}
			/>
		</Grid>
	);
};

const CompensationSection = ({
	model = undefined,
	selected = null,
	isLoading = false,
	overrideCompensation = false,
	handleSummaryChange = () => {},
	...rest
}: Readonly<ITechniciansWashoutContent & GridProps>) => {
	if (!model?.summary?.summary && !isLoading) {
		return null;
	}

	const isEditable = useIsWashoutEditable({
		cycleStatus: selected?.status ?? "",
		positionStatus: model?.review?.status ?? "",
		hasRole: overrideCompensation,
	});

	return (
		<Grid item {...rest}>
			<Compensation
				data={model?.summary?.summary ?? []}
				addedData={model?.addedCompensations ?? []}
				tooltip
				editable={isEditable}
				columns={[
					{
						field: "tier",
						headerName: "Total Hrs/Gross",
						align: "right",
						headerAlign: "right",
						flex: 1,
					},
					{
						field: "rate",
						headerName: "Eff Pay Rate",
						align: "right",
						headerAlign: "left",
						cellsType: "dollar",
						width: 120,
					},
				]}
				itemsDefinitions={[
					{ item: "Spiff", type: "percentage" },
					{ item: "Bonus", type: "percentage" },
					{ item: "Total Extended Pay", type: "hours", columns: ["tier"] },
					{ item: "Night Shift Pay", type: "hours", columns: ["tier"] },
				]}
				dataPresent={Boolean(model)}
				loading={isLoading}
				onActionUpdate={handleSummaryChange}
			/>
		</Grid>
	);
};

const SpiffBonusSection = ({ model = undefined, isLoading = false, ...rest }: Readonly<ITechniciansWashoutContent & GridProps>) => {
	if (!model?.spiffs?.data && !isLoading) {
		return null;
	}
	return (
		<Grid item {...rest}>
			<Grid>
				<SpiffsBonus
					data={model?.spiffs?.data}
					dataPresent={Boolean(model)}
					loading={isLoading}
					tableProps={{ ...BASE_TABLE_PROPS }}
				/>
			</Grid>
		</Grid>
	);
};

const TechniciansWashoutContent = (props: Readonly<ITechniciansWashoutContent>) => {
	const {
		model = undefined,
		selected,
		isLoading = false,
		hideEmployeeMeta = false,
		overrideCompensation = false,
		editable = false,
		handleSummaryChange = () => {},
		disableBoxShadow = false,
	} = props;
	return (
		<Grid
			container
			direction="column"
			className={clsx({ "shadow-white-section": !disableBoxShadow })}
			rowSpacing={0}
			sx={{
				minHeight: model || isLoading ? "630px" : "0px",
			}}
		>
			<EmployeeMetaSection
				{...props.employeeMetaProps}
				model={model}
				hidden={hideEmployeeMeta}
				loading={isLoading}
				secondaryColumns={["DAYS_OUT", "RANKING"]}
			/>
			<PerformanceSection model={model} isLoading={isLoading} />
			<PendingRepairOrdersSection model={model} isLoading={isLoading} />

			<Grid
				item
				container
				className={clsx({
					hidden: !model && !isLoading,
					"px-[32px] pt-[22px] pb-[48px]": true,
					"grid-cols-2 gap-x-[48px] gap-y-[32px] justify-end": true,
				})}
			>
				<LongevitySection className="w-[644px]" model={model} isLoading={isLoading} />
				<SpiffBonusSection className="w-[644px]" model={model} isLoading={isLoading} />
				<CompensationSection
					className="w-[644px] !mt-[5px]"
					model={model}
					selected={selected}
					isLoading={isLoading}
					overrideCompensation={overrideCompensation}
					handleSummaryChange={handleSummaryChange}
					editable={editable}
				/>
			</Grid>
		</Grid>
	);
};

export default TechniciansWashoutContent;
